import type { BoxProps } from "@mui/material";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { DragEventHandler } from "react";
import { useRef, useState } from "react";
import { makeTintedBackgroundFromColor } from "../utilities/colorHelper";
import HiddenFileInput from "./inputs/HiddenFileInput";

interface Props {
  acceptedFileExtensions: string[];
  subtitle?: string;
  subtitleDescription?: string;
  onFilesAdd: (files: File[]) => void;
  allowMultiple?: boolean;
  disabled?: boolean;
  containerProps?: BoxProps;
}

const FilesDropArea = ({
  acceptedFileExtensions,
  subtitle,
  subtitleDescription,
  onFilesAdd,
  allowMultiple,
  disabled,
  containerProps = {},
}: Props) => {
  const [isActive, setIsActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // dragleave also fires when the pointer moves onto a child, so count entries instead of trusting single events.
  const dragDepthRef = useRef(0);

  const handleDragOver: DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Allow drag & drop of files from the browsing window
    e.dataTransfer.dropEffect = disabled ? "none" : "link";
  };

  const handleDrop: DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsActive(false);

    if (disabled) {
      return;
    }

    onFilesAdd(Array.from(e.dataTransfer.files));
  };

  const handleDragEnter: DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current++;
    setIsActive(!disabled);
  };

  const handleDragLeave: DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current--;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsActive(false);
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <HiddenFileInput
        ref={fileInputRef}
        acceptedFileTypes={acceptedFileExtensions}
        multiple={allowMultiple}
        onFilesAdd={onFilesAdd}
      />
      <Box
        {...containerProps}
        onDrop={handleDrop}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        sx={[
          {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
            py: 2,
            width: "100%",
          },
          (t) => ({
            borderWidth: 1,
            borderStyle: isActive ? "solid" : "dashed",
            borderColor: isActive ? t.palette.primary.main : t.palette.divider,
            borderRadius: "4px",
            backgroundColor: isActive ? makeTintedBackgroundFromColor(t, t.palette.primary.main) : undefined,
          }),
          ...(Array.isArray(containerProps.sx) ? containerProps.sx : [containerProps.sx]),
        ]}
      >
        <Typography variant="subtitle2">
          Drag and drop here or
          <Button
            variant="text"
            color="primary"
            disabled={disabled}
            onClick={handleBrowseFiles}
            sx={{ pl: "4px", pt: 0, pb: "1px", pr: 0 }}
          >
            browse files
          </Button>
        </Typography>
        {(subtitle || subtitleDescription) && (
          <Stack spacing={1} sx={{ mt: 0.5 }}>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}>
                {subtitle}
              </Typography>
            )}
            {subtitleDescription && (
              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "pre-wrap", textAlign: "center" }}>
                {subtitleDescription}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </>
  );
};

export default FilesDropArea;
